/**
 * Pane 1 — Photos. Selectable photo cards (tap · shift-range · ⌘/ctrl-add ·
 * rubber-band box drag-select · Select all / Clear selection), a Sort control
 * (To-do first / Upload order / Filename / Largest) and contextual bulk actions
 * (Auto-crop all / Fit all · White border all / Edge all). A slim banner
 * summarizes any auto-cropped / low-res prints and opens the Review modal (the
 * 200-photo scaling fix — no inline warning wall). Each card lists its prints
 * as removable chips; the card ✕ removes the photo (confirmed). A magnifier
 * (or double-click) opens the LIGHTBOX to view a photo large while selecting.
 *
 * Box-select is PURE GEOMETRY: the final drag rectangle (from the actual
 * pointer-up coordinates) is intersected with each tile's live bounding box —
 * never an index range — and zero-size rects (content-visibility skipped
 * layouts) are ignored. See lib/sizePaper.boxSelectHits.
 */

import { borderModesFor, type BorderMode, type PricingData, type ResolvedSize } from '@rpl/pricing';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ratioFit } from '../../lib/printCheck';
import {
  boxSelectHits,
  flaggedForReview,
  normalizeDragRect,
  photosWithPrintsSet,
  sortPhotos,
  wizardChipVisible,
  type PixelRect,
} from '../../lib/sizePaper';
import type { LocalPhoto, SortMode, UiPrint } from '../../lib/types';
import { useStore } from '../../store/store';
import { Lightbox } from './Lightbox';
import { ModalShell } from './ModalShell';
import { PhotoTile } from './PhotoTile';
import type { OrderActions } from './useOrderActions';

export interface PhotosPaneProps {
  pricing: PricingData;
  catalog: Map<string, ResolvedSize>;
  actions: OrderActions;
  onOpenCrop: (printId: string) => void;
  /** Open the per-image print editor in "add a print" mode (tile ✎ / lightbox). */
  onAddPrint: (photoId: string) => void;
  /** Open the Easy Print Wizard (persistent ≥40-photo chip — round 4). */
  onOpenWizard: () => void;
  onReview: () => void;
  onToast: (msg: string) => void;
  /** Adaptive desktop width tier (owner 2026-07-27 v2): 1-col rail <10
   *  photos · 2-col rail 10-40 · full 40+ (rails have no toolbar — expand
   *  for the full grid). Mobile always passes 'full'. */
  paneMode?: 'rail' | 'rail2' | 'full';
  /** Chevron: collapsed ⇄ full; a second press returns to the auto tier. */
  onTogglePane?: () => void;
}

const DRAG_THRESHOLD = 6;

export function PhotosPane({
  pricing,
  catalog,
  actions,
  onOpenCrop,
  onAddPrint,
  onOpenWizard,
  onReview,
  onToast,
  paneMode = 'full',
  onTogglePane,
}: PhotosPaneProps) {
  const photos = useStore((s) => s.photos);
  const prints = useStore((s) => s.prints);
  const selection = useStore((s) => s.selection);
  const sort = useStore((s) => s.sortSp);
  const setSort = useStore((s) => s.setSortSp);
  const toggleSelect = useStore((s) => s.toggleSelect);
  const setSelection = useStore((s) => s.setSelection);
  const selectAll = useStore((s) => s.selectAll);
  const clearSelection = useStore((s) => s.clearSelection);
  const removePhoto = useStore((s) => s.removePhoto);
  const removePrint = useStore((s) => s.removePrint);
  const setStep = useStore((s) => s.setStep);

  const withPrints = useMemo(() => photosWithPrintsSet(prints), [prints]);
  const ready = useMemo(
    () => sortPhotos(photos.filter((p) => p.status === 'ready'), sort, withPrints),
    [photos, sort, withPrints],
  );
  // Print Check chip filter removed (owner 2026-07-27) — all ready photos show.
  const visible = ready;
  const printsByPhoto = useMemo(() => {
    const map = new Map<string, UiPrint[]>();
    for (const p of prints) (map.get(p.photoId) ?? map.set(p.photoId, []).get(p.photoId)!).push(p);
    return map;
  }, [prints]);

  const readyCount = ready.length;
  const selectedCount = selection.size;
  const allSelected = readyCount > 0 && selectedCount >= readyCount;

  // Bulk-action eligibility (geometry-based so the crop/fit toggle works both ways).
  const hasRatio = useMemo(
    () =>
      prints.some((p) => {
        if (p.frameId) return false;
        const entry = catalog.get(p.sizeId);
        const photo = photos.find((ph) => ph.id === p.photoId);
        if (!entry || !photo) return false;
        return ratioFit(photo.width, photo.height, entry.inches, actions.thresholds.ratioTolerance) === 'autocrop';
      }),
    [prints, catalog, photos, actions.thresholds],
  );
  const hasLoose = prints.some((p) => !p.frameId);
  const reviewCount = useMemo(() => flaggedForReview(prints).length, [prints]);

  /** Bulk-apply a border to the selection's loose prints, or all if none selected. */
  const applyBulkBorder = (mode: BorderMode) => {
    const scope: 'selected' | 'all' = selection.size > 0 ? 'selected' : 'all';
    const affected = prints.filter(
      (p) =>
        !p.frameId &&
        (scope === 'all' || selection.has(p.photoId)) &&
        borderModesFor(p.sizeId, false).includes(mode),
    );
    if (affected.length === 0) return;
    actions.bulkSetBorder(mode, scope);
    const what = mode === 'white' ? 'White border' : 'Print-to-edge';
    onToast(
      `${what} applied to ${affected.length} print${affected.length === 1 ? '' : 's'}${
        scope === 'selected' ? ' in your selection' : ''
      }`,
    );
  };

  /* ---------- selection: tap + shift-range ---------- */
  const lastIndexRef = useRef<number | null>(null);
  const suppressClickRef = useRef(false);


  const handleToggle = (photoId: string, e: ReactMouseEvent) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    // Range-select over the VISIBLE rows so a chip-filtered grid never sweeps
    // in photos that are hidden between the endpoints.
    const index = visible.findIndex((p) => p.id === photoId);
    if (e.shiftKey && lastIndexRef.current !== null && index !== -1) {
      // Clamp defensively: the anchor may predate a list shrink (photo removal).
      const [from, to] = [Math.min(lastIndexRef.current, index), Math.max(lastIndexRef.current, index)];
      const next = new Set(selection);
      for (let i = from; i <= Math.min(to, visible.length - 1); i++) next.add(visible[i].id);
      setSelection(next);
    } else {
      toggleSelect(photoId);
    }
    lastIndexRef.current = index;
  };

  /* ---------- rubber-band box select ---------- */
  const gridRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{ x: number; y: number; add: boolean } | null>(null);
  const [marquee, setMarquee] = useState<PixelRect | null>(null);
  const marqueeLive = useRef(false); // avoids stale state at pointer-up

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    suppressClickRef.current = false;
    // Mouse-only: touch stays free for tap-to-select and vertical scrolling.
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    dragStart.current = { x: e.clientX, y: e.clientY, add: e.shiftKey || e.metaKey || e.ctrlKey };
    marqueeLive.current = false;
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const s = dragStart.current;
    if (!s) return;
    const box = normalizeDragRect(s.x, s.y, e.clientX, e.clientY);
    if (!marqueeLive.current && box.w < DRAG_THRESHOLD && box.h < DRAG_THRESHOLD) return;
    if (!marqueeLive.current) {
      marqueeLive.current = true;
      try {
        gridRef.current?.setPointerCapture(e.pointerId);
      } catch {
        /* capture unsupported */
      }
    }
    setMarquee(box);
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const s = dragStart.current;
    dragStart.current = null;
    try {
      gridRef.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* not captured */
    }
    if (!s || !marqueeLive.current) {
      marqueeLive.current = false;
      setMarquee(null);
      return;
    }
    marqueeLive.current = false;
    // Final box from the ACTUAL pointer-up coordinates (never a stale render).
    const box = normalizeDragRect(s.x, s.y, e.clientX, e.clientY);
    const tiles = Array.from(gridRef.current?.querySelectorAll<HTMLElement>('[data-tile-id]') ?? []).map(
      (el) => {
        const r = el.getBoundingClientRect();
        return { id: el.dataset.tileId!, rect: { x: r.left, y: r.top, w: r.width, h: r.height } };
      },
    );
    const hits = boxSelectHits(box, tiles); // strict 2D overlap; zero-size rects skipped
    setSelection(s.add ? new Set([...selection, ...hits]) : new Set(hits));
    suppressClickRef.current = true;
    setMarquee(null);
  };

  const marqueeStyle = () => {
    const grid = gridRef.current;
    if (!marquee || !grid) return undefined;
    const g = grid.getBoundingClientRect();
    // Absolute children of the scroll container live in CONTENT coordinates —
    // add the scroll offsets so the visible box tracks the pointer when scrolled.
    return {
      left: marquee.x - g.left + grid.scrollLeft,
      top: marquee.y - g.top + grid.scrollTop,
      width: marquee.w,
      height: marquee.h,
    };
  };

  /* ---------- lightbox (view large while selecting) ---------- */
  const [lightboxId, setLightboxId] = useState<string | null>(null);

  /* ---------- remove-photo confirm ---------- */
  const [confirm, setConfirm] = useState<LocalPhoto | null>(null);

  /* ---------- bulk-delete confirm (snapshot of the selection) ---------- */
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);
  const doBulkDelete = () => {
    if (!confirmDelete) return;
    for (const id of confirmDelete) removePhoto(id);
    setConfirmDelete(null);
  };

  /* RAIL mode (owner 2026-07-27, ≤3 photos or manual collapse): a slim thumb
     strip with selection ticks — no toolbar (sort is meaningless at this
     count, Select all saves ≤2 taps, everything else lives one chevron-click
     away in the full grid). Double-click still opens the lightbox. */
  if (paneMode === 'rail' || paneMode === 'rail2') {
    return (
      <section className={`pane pane--photos pane--rail${paneMode === 'rail2' ? ' pane--rail2' : ''}`}>
        <button
          type="button"
          className="rail__expand"
          data-tip="Expand the photo grid"
          aria-label="Expand the photos pane"
          onClick={onTogglePane}
        >
          {/* Owner 2026-07-27: chevron only — a label read as if the photos
              below weren't already the photos. */}
          <span aria-hidden>»</span>
        </button>
        <div className="rail__list">
          {ready.map((photo) => {
            const on = selection.has(photo.id);
            // Owner 2026-07-27: show what's already ORDERED right on the rail.
            const photoPrints = printsByPhoto.get(photo.id) ?? [];
            const orderedQty = photoPrints.reduce((n, p) => n + p.qty, 0);
            return (
              <button
                key={photo.id}
                type="button"
                className={`rail__thumb${on ? ' rail__thumb--on' : ''}`}
                title={
                  orderedQty > 0
                    ? `${photo.filename} · ${orderedQty} print${orderedQty === 1 ? '' : 's'} in your order`
                    : photo.filename
                }
                aria-pressed={on}
                onClick={() => toggleSelect(photo.id)}
                onDoubleClick={() => setLightboxId(photo.id)}
              >
                {photo.previewUrl ? (
                  <img src={photo.previewUrl} alt={photo.filename} draggable={false} />
                ) : (
                  <span className="rail__ph" />
                )}
                <span className={`rail__tick${on ? ' rail__tick--on' : ''}`} aria-hidden>
                  ✓
                </span>
                {orderedQty > 0 && (
                  <span
                    className="rail__have"
                    aria-label={`${orderedQty} print${orderedQty === 1 ? '' : 's'} in your order`}
                  >
                    ▤ {orderedQty}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {lightboxId && (
          <Lightbox
            photos={ready}
            photoId={lightboxId}
            selection={selection}
            onNavigate={setLightboxId}
            onToggleSelect={toggleSelect}
            onOrderPrint={(photoId) => {
              setLightboxId(null);
              onAddPrint(photoId);
            }}
            onClose={() => setLightboxId(null)}
          />
        )}
      </section>
    );
  }

  return (
    <section className="pane pane--photos">
      <header className="pane__head">
        {/* mobile-only (CSS-gated): the STEP N OF 4 header row is gone on phones,
            so the pane title carries the way back to Upload */}
        <button
          type="button"
          className="pane__back"
          onClick={() => setStep(0)}
          aria-label="Back to upload"
        >
          ‹
        </button>
        <h2 className="pane__title">Photos</h2>
        <div className="pane__head-tools">
          <button type="button" className="linkbtn" onClick={selectAll} disabled={readyCount === 0 || allSelected}>
            Select all
          </button>
          <button type="button" className="linkbtn" onClick={clearSelection} disabled={selectedCount === 0}>
            Clear selection
          </button>
          <label className="linkbtn photos__sort">
            Sort:{' '}
            <select value={sort} onChange={(e) => setSort(e.target.value as SortMode)} aria-label="Sort photos">
              <option value="todo">To-do first</option>
              <option value="upload">Upload order</option>
              <option value="name">Filename A–Z</option>
              <option value="mp-desc">Largest first</option>
            </select>
          </label>
          {onTogglePane && (
            <button
              type="button"
              className="linkbtn rail__collapse"
              data-tip="Minimise to a photo rail"
              aria-label="Minimise the photos pane"
              onClick={onTogglePane}
            >
              «
            </button>
          )}
        </div>
      </header>

      {/* Owner 2026-07-27: selecting EVERYTHING also surfaces the wizard —
          the classic "same prints for the whole roll" intent. */}
      {(wizardChipVisible(readyCount) || (allSelected && readyCount >= 2)) && (
        <div className="photos__wizrow">
          <button
            type="button"
            className="photos__wizchip"
            title="Set one size, paper and quantity for every photo"
            onClick={onOpenWizard}
          >
            ✨ Bulk order? Easy Print Wizard
          </button>
        </div>
      )}

      {reviewCount > 0 && (
        <div className="photos__review">
          <span className="photos__review-text">
            {reviewCount} print{reviewCount === 1 ? '' : 's'} auto-cropped or need a fix
          </span>
          <button type="button" className="pill-btn photos__review-btn" onClick={onReview}>
            Review crops
          </button>
        </div>
      )}

      {(hasRatio || hasLoose || selectedCount >= 2) && (
        <div className="photos__bulk" role="group" aria-label="Bulk actions">
          <span className="photos__bulk-label">Bulk{selectedCount > 0 ? ` (${selectedCount} selected)` : ''}:</span>
          {hasRatio && (
            <>
              <button type="button" className="pill-btn" onClick={() => actions.bulkRatio('crop')}>
                Auto-crop all
              </button>
              <button type="button" className="pill-btn" onClick={() => actions.bulkRatio('fit')}>
                Fit all
              </button>
            </>
          )}
          {(hasRatio || hasLoose) && (
            <>
              <button type="button" className="pill-btn" disabled={!hasLoose} onClick={() => applyBulkBorder('white')}>
                White border all
              </button>
              <button type="button" className="pill-btn" disabled={!hasLoose} onClick={() => applyBulkBorder('none')}>
                Edge all
              </button>
            </>
          )}
          {selectedCount >= 2 && (
            <button
              type="button"
              className="pill-btn pill-btn--danger"
              onClick={() => setConfirmDelete([...selection])}
            >
              Delete ({selectedCount})
            </button>
          )}
        </div>
      )}

      {readyCount === 0 ? (
        <div className="pane__empty">No photos ready yet.</div>
      ) : (
        <div
          ref={gridRef}
          className="photos__grid"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {visible.map((photo) => (
            <PhotoTile
              key={photo.id}
              photo={photo}
              prints={printsByPhoto.get(photo.id) ?? []}
              selected={selection.has(photo.id)}
              pricing={pricing}
              catalog={catalog}
              onToggle={(e) => handleToggle(photo.id, e)}
              onOpenCrop={onOpenCrop}
              onZoom={() => setLightboxId(photo.id)}
              onRemoveChip={(printId) => removePrint(printId)}
              onRemovePhoto={() => setConfirm(photo)}
            />
          ))}
          {/* Mobile only, CSS-gated (owner 2026-07-27): the way back to Upload
              is the ‹ in the pane head, which is easy to miss. "Add more" is
              an intent that lands at the END of the roll, so the tile goes
              there. Desktop keeps the header stepper. */}
          <button
            type="button"
            className="photos__more"
            onClick={() => setStep(0)}
            aria-label="Upload more photos"
          >
            <span className="photos__more-plus" aria-hidden>
              +
            </span>
            <span className="photos__more-label">Upload more</span>
          </button>
          {marquee && <div className="photos__marquee" style={marqueeStyle()} aria-hidden />}
        </div>
      )}

      {lightboxId && (
        <Lightbox
          photos={visible}
          photoId={lightboxId}
          selection={selection}
          onNavigate={setLightboxId}
          onToggleSelect={toggleSelect}
          onOrderPrint={(photoId) => {
            setLightboxId(null); // close INTO the Customise pane (owner 2026-07-27)
            onAddPrint(photoId);
          }}
          onClose={() => setLightboxId(null)}
        />
      )}

      {confirmDelete && (
        <ModalShell onClose={() => setConfirmDelete(null)} width={420} label="Delete photos" className="confirm">
          <div className="confirm__body">
            <h3 className="confirm__title">
              Delete {confirmDelete.length} photo{confirmDelete.length === 1 ? '' : 's'}?
            </h3>
            <p className="confirm__text">
              You&rsquo;ll need to re-upload them. Any print sizes on them will be removed too.
            </p>
            <div className="confirm__actions">
              <button type="button" className="btn btn--secondary" onClick={() => setConfirmDelete(null)}>
                Keep them
              </button>
              <button type="button" className="btn btn--primary confirm__danger" onClick={doBulkDelete}>
                Delete {confirmDelete.length}
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {confirm && (
        <ModalShell onClose={() => setConfirm(null)} width={420} label="Remove photo" className="confirm">
          <div className="confirm__body">
            <h3 className="confirm__title">Remove this photo?</h3>
            <p className="confirm__text">
              You’ll need to re-upload {confirm.filename} to get it back. Any print sizes on it will be
              removed too.
            </p>
            <div className="confirm__actions">
              <button type="button" className="btn btn--secondary" onClick={() => setConfirm(null)}>
                Keep it
              </button>
              <button
                type="button"
                className="btn btn--primary confirm__danger"
                onClick={() => {
                  removePhoto(confirm.id);
                  setConfirm(null);
                }}
              >
                Remove photo
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </section>
  );
}
